import { useEffect, useState } from 'react';
import type { RoleDefinition } from '../../../app/roles';
import type { PresenterDepth } from '../../../domain';
import { MaskedValue } from './MaskedValue';

const landingTab: Record<PresenterDepth, string> = {
  overview: 'overview',
  operations: 'assignment',
  architecture: 'technical',
};

export function PhoneTabs({ asset, role, depth }: { asset: any, role: RoleDefinition, depth: PresenterDepth }) {
  const [activeTab, setActiveTab] = useState(landingTab[depth]);

  const allTabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'assignment', label: 'Assignment' },
    { id: 'technical', label: 'Technical' },
    { id: 'maintenance', label: 'Maintenance' },
    { id: 'history', label: 'History' },
    { id: 'attachments', label: 'Attachments' },
  ];
  const tabs = allTabs.filter((tab) => {
    if (depth === 'overview') return ['overview', 'assignment', 'history'].includes(tab.id);
    if (depth === 'architecture') return ['overview', 'assignment', 'technical', 'history'].includes(tab.id);
    return true;
  });

  useEffect(() => {
    setActiveTab(landingTab[depth]);
  }, [depth]);

  return (
    <div className="phone-tabs-container">
      <p className="phone-depth-note" role="status">
        {depth === 'overview' && 'Overview opens the device outcome and keeps secondary technical tabs out of the way.'}
        {depth === 'operations' && 'Operations opens assignment work and exposes the complete lifecycle tab set.'}
        {depth === 'architecture' && 'Architecture opens permitted technical identifiers and record relationships.'}
      </p>
      <div aria-label="Phone record sections" className="tabs-header" role="tablist">
        {tabs.map(tab => (
          <button
            aria-selected={activeTab === tab.id}
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            role="tab"
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="tab-content" role="tabpanel">
        {activeTab === 'overview' && (
          <div className="tab-pane">
            <h3>Overview</h3>
            <p><strong>Type:</strong> Smartphone</p>
            <p><strong>Manufacturer:</strong> Apple</p>
            <p><strong>Model:</strong> iPhone 13 Pro</p>
            <p><strong>Storage:</strong> 256GB</p>
          </div>
        )}
        {activeTab === 'technical' && (
          <div className="tab-pane">
            <h3>Technical Identifiers</h3>
            <div className="spec-row">
              <strong>Serial Number:</strong> <MaskedValue value={asset.serialNumber || 'C02W9308J1G6'} role={role} type="serial" />
            </div>
            <div className="spec-row">
              <strong>IMEI:</strong> <MaskedValue value="358921008891234" role={role} type="imei" />
            </div>
            <div className="spec-row">
              <strong>MAC Address:</strong> <MaskedValue value="00:1A:2B:3C:4D:5E" role={role} type="mac" />
            </div>
          </div>
        )}
        {activeTab === 'assignment' && (
          <div className="tab-pane">
            <h3>Assignment</h3>
            <p>Assigned to {asset.assigneeName || 'None'}</p>
            <p>Expected return: Never</p>
          </div>
        )}
        {activeTab === 'maintenance' && (
          <div className="tab-pane">
            <h3>Maintenance</h3>
            <p>No active maintenance tasks.</p>
          </div>
        )}
        {activeTab === 'history' && (
          <div className="tab-pane">
            <h3>History</h3>
            <ul className="history-list">
              <li>
                <strong>State changed</strong> from <code>available</code> to <code>assigned</code>
                <div className="subtext">Reason: New hire provisioning</div>
              </li>
            </ul>
          </div>
        )}
        {activeTab === 'attachments' && (
          <div className="tab-pane">
            <h3>Attachments</h3>
            <p>No attachments found.</p>
          </div>
        )}
      </div>
    </div>
  );
}
